import { format, isValid, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { capitalize } from 'inflected';
import * as R from 'ramda';
import {
  createDefaultCrossChannelMetrics,
  CrossChannelMetrics,
  Metric,
  Metrics,
  MetricTotalsByDate,
  NativeCurrencyMetric
} from '@blisspointmedia/bpm-types/dist/CrossChannel';
import * as CC from '@blisspointmedia/bpm-types/dist/CrossChannelPerformance';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { IconSpec } from '@blisspointmedia/bpm-types/dist/dimensionIcons';
import { DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import {
  getCompanyChannelColumnMetaData,
  sharedColumnMetaDataMap,
  sharedDimensionColumnMetaDataMap
} from '@Pages/SingleChannel/Channels/shared-performance-utils';
import {
  CellData,
  ColumnMetaData,
  DimensionMap
} from '@src/Components/MetricsTable/metrics-table-utils';
import { getDimensionIcon, getDimensionIconCell } from '@src/utils/columnMetricSystem/adapters';
import { DAY_DATE_LABEL, formatDateLabel } from '@src/utils/date-utils';
import { withRatioAggregators } from '@src/utils/metrics/ratioAggregation';
import {
  calculateAOV,
  calculateCCR,
  calculateCPC,
  calculateCPM,
  calculateCPX,
  calculateCTR,
  calculateICR,
  calculateROAS
} from '@src/utils/metric-utils';
import { METRIC_TO_PRETTY_NAME } from './cross-channel-constants';

const {
  AOV,
  CLICKS,
  CLICKS_CONVERSION_RATE,
  CPC,
  CPM,
  CPX,
  CTR,
  IMPS_CONVERSION_RATE,
  IMPRESSIONS,
  REVENUE,
  ROAS,
  SPEND,
  VOLUME,
  VIDEO_VIEWS,
  VIDEOS_WATCHED_AT_25P,
  VIDEOS_WATCHED_AT_50P,
  VIDEOS_WATCHED_AT_75P,
  VIDEOS_WATCHED_AT_100P
} = Metric;

const { CPC_NATIVE, CPM_NATIVE, CPX_NATIVE, SPEND_NATIVE, REVENUE_NATIVE } = NativeCurrencyMetric;
const {
  [SPEND_NATIVE]: spendNativeMetaData,
  [REVENUE_NATIVE]: revenueNativeMetaData,
  [CPX_NATIVE]: cpxNativeMetaData,
  [CPM_NATIVE]: cpmNativeMetaData,
  [CPC_NATIVE]: cpcNativeMetaData,
  [VIDEO_VIEWS]: videoViewsMetaData,
  [VIDEOS_WATCHED_AT_25P]: videosWatchedAt25pMetaData,
  [VIDEOS_WATCHED_AT_50P]: videosWatchedAt50pMetaData,
  [VIDEOS_WATCHED_AT_75P]: videosWatchedAt75pMetaData,
  [VIDEOS_WATCHED_AT_100P]: videosWatchedAt100pMetaData,
  [CLICKS]: clicksMetaData,
  [IMPRESSIONS]: impressionsMetaData,
  [SPEND]: spendMetaData,
  [REVENUE]: revenueMetaData,
  [ROAS]: roasMetaData,
  [VOLUME]: volumeMetaData,
  [CPM]: cpmMetaData,
  [CPX]: cpxMetaData,
  [CTR]: ctrMetaData,
  [CPC]: cpcMetaData,
  [CLICKS_CONVERSION_RATE]: clicksConversionRateMetaData,
  [IMPS_CONVERSION_RATE]: impsConversionRateMetaData,
  [AOV]: aovMetaData
} = sharedColumnMetaDataMap;

const prettyDateRangeFormatDateLabel = (date: string): string => {
  if (!date) {
    return '';
  }

  const toUTC = toZonedTime(new Date(date), 'UTC');

  if (!isValid(toUTC)) {
    return '';
  }

  return format(toUTC, DAY_DATE_LABEL);
};

export const makePrettyDateRange = (start: string, end: string): string =>
  `${prettyDateRangeFormatDateLabel(start)}–${prettyDateRangeFormatDateLabel(end)}`;

/**
 * Linear TV weeks run Monday-Sunday and don't finalize until the following Friday, so a tilde
 * ("~", meaning "approximate") should show next to metrics whenever the selected date range
 * overlaps the current week, or the previous week before it has finalized (Mon-Thu).
 */
export const shouldShowTilde = (dates: { start: string; end: string } | undefined): boolean => {
  if (!dates?.start || !dates?.end) {
    return false;
  }

  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat

  const daysFromMonday = (dayOfWeek + 6) % 7;
  const currentWeekStart = new Date(today);
  currentWeekStart.setDate(today.getDate() - daysFromMonday);
  currentWeekStart.setHours(0, 0, 0, 0);

  const currentWeekEnd = new Date(currentWeekStart);
  currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
  currentWeekEnd.setHours(23, 59, 59, 999);

  const rangeStart = parseISO(dates.start);
  const rangeEnd = parseISO(dates.end);

  if (rangeStart <= currentWeekEnd && rangeEnd >= currentWeekStart) {
    return true;
  }

  if (dayOfWeek >= 1 && dayOfWeek <= 4) {
    const prevWeekStart = new Date(currentWeekStart);
    prevWeekStart.setDate(currentWeekStart.getDate() - 7);
    const prevWeekEnd = new Date(prevWeekStart);
    prevWeekEnd.setDate(prevWeekStart.getDate() + 6);
    prevWeekEnd.setHours(23, 59, 59, 999);

    if (rangeStart <= prevWeekEnd && rangeEnd >= prevWeekStart) {
      return true;
    }
  }

  return false;
};

/**
 * Aggregate platform delivery metrics by day for the Overview tab.
 */
export const calculateOverviewMetricsByDay = (
  metricsData: CrossChannelMetrics[]
): MetricTotalsByDate[] => {
  if (R.isNil(metricsData) || R.isEmpty(metricsData)) {
    return [];
  }

  // Sum metric totals by day
  const metricsByDay: Record<string, Metrics> = {};
  for (const item of metricsData) {
    const { date, metrics } = item;
    metricsByDay[date] = R.mergeWith(R.add, metricsByDay[date] || {}, metrics);
  }

  // Calculate derived metrics by day
  const allMetricsByDay = Object.entries(metricsByDay).map(([date, metrics]) => {
    const spend = R.pathOr(0, [date, SPEND], metricsByDay);
    const clicks = R.pathOr(0, [date, CLICKS], metricsByDay);
    const impressions = R.pathOr(0, [date, IMPRESSIONS], metricsByDay);
    const volume = R.pathOr(0, [date, VOLUME], metricsByDay);
    const revenue = R.pathOr(0, [date, REVENUE], metricsByDay);

    const computedMetrics = {};
    computedMetrics[CPC] = calculateCPC(spend, clicks);
    computedMetrics[CPM] = calculateCPM(spend, impressions);
    computedMetrics[CTR] = calculateCTR(clicks, impressions);
    computedMetrics[CPX] = calculateCPX(spend, volume);
    computedMetrics[ROAS] = calculateROAS(revenue, spend);
    computedMetrics[AOV] = calculateAOV(revenue, volume);
    computedMetrics[CLICKS_CONVERSION_RATE] = calculateCCR(volume, clicks);
    computedMetrics[IMPS_CONVERSION_RATE] = calculateICR(volume, impressions);

    return { date, metrics: { ...metrics, ...computedMetrics } };
  });

  return allMetricsByDay.sort((a, b) => a.date.localeCompare(b.date));
};

export const getPrettyMetricName = (metric: string): string =>
  METRIC_TO_PRETTY_NAME[metric] || metric;

export const makeColumnMetaData = (
  metric: CC.DimensionColumnType,
  iconStyle?: IconSpec['style']
): ColumnMetaData => ({
  contentType: 'text',
  name: metric,
  prettyName: metric,
  iconStyle: iconStyle || undefined
});

// pretty dimension name -> master column name for the icon spec
const CROSS_CHANNEL_ICON_COLUMNS: Partial<Record<string, string>> = {
  Channel: 'channel',
  Platform: 'platform'
};

const crossChannelIconStyle = (dimensionTypeName: string): IconSpec['style'] | undefined => {
  const iconColumn = CROSS_CHANNEL_ICON_COLUMNS[dimensionTypeName];
  return iconColumn ? getDimensionIcon(iconColumn)?.style : undefined;
};

export const dimensionColumnMetaDataMap: Partial<
  Record<CC.DimensionColumnType | string, ColumnMetaData>
> = {
  Platform: makeColumnMetaData('Platform', crossChannelIconStyle('Platform')),
  Channel: makeColumnMetaData('Channel', crossChannelIconStyle('Channel')),
  'Account Name': makeColumnMetaData('Account Name'),
  'Account ID': makeColumnMetaData('Account ID'),
  Surface: makeColumnMetaData('Surface'),
  'Campaign Name': makeColumnMetaData('Campaign Name'),
  'Campaign ID': makeColumnMetaData('Campaign ID'),
  ...sharedDimensionColumnMetaDataMap
};

/** Dimension meta entries for a company's custom segments, derived from a filter-options payload. */
export const buildCustomSegmentDimensionMap = (
  filterOptions: CC.GetFilterOptionsResponse
): Record<string, ColumnMetaData> =>
  filterOptions
    .filter(option => option.isCustomSegment)
    .reduce(
      (acc, option) => ({
        ...acc,
        [option.label]: makeColumnMetaData(option.label as CC.DimensionColumnType)
      }),
      {} as Record<string, ColumnMetaData>
    );

export const columnMetaDataMap: Partial<Record<CC.ColumnType, ColumnMetaData>> = {
  [AOV]: aovMetaData,
  [CLICKS]: clicksMetaData,
  [SPEND]: spendMetaData,
  [IMPRESSIONS]: impressionsMetaData,
  [REVENUE]: revenueMetaData,
  [ROAS]: roasMetaData,
  [VOLUME]: volumeMetaData,
  [CPM]: cpmMetaData,
  [CPX]: cpxMetaData,
  [CLICKS_CONVERSION_RATE]: clicksConversionRateMetaData,
  [IMPS_CONVERSION_RATE]: impsConversionRateMetaData,
  [CTR]: ctrMetaData,
  [CPC]: cpcMetaData,
  [SPEND_NATIVE]: spendNativeMetaData,
  [REVENUE_NATIVE]: revenueNativeMetaData,
  [CPC_NATIVE]: cpcNativeMetaData,
  [CPM_NATIVE]: cpmNativeMetaData,
  [CPX_NATIVE]: cpxNativeMetaData,
  [VIDEO_VIEWS]: videoViewsMetaData,
  [VIDEOS_WATCHED_AT_25P]: videosWatchedAt25pMetaData,
  [VIDEOS_WATCHED_AT_50P]: videosWatchedAt50pMetaData,
  [VIDEOS_WATCHED_AT_75P]: videosWatchedAt75pMetaData,
  [VIDEOS_WATCHED_AT_100P]: videosWatchedAt100pMetaData
};

export const getCrossChannelDimensionCell = (
  dimensionData: DimensionMap,
  dimensionHeader: DimensionColumn,
  fiscalCal: CompanyFiscalCal
): CellData => {
  const { dimensionVarName, dimensionTypeName, icon } = dimensionHeader;
  const resolvedDimensionData = dimensionData as Record<string, string>;
  const dimensionValue = resolvedDimensionData[dimensionVarName];
  const defaultCell = {
    label: dimensionValue,
    value: dimensionValue
  };
  const iconColumn = CROSS_CHANNEL_ICON_COLUMNS[dimensionTypeName];
  const iconCell =
    iconColumn && !R.isNil(icon)
      ? getDimensionIconCell(iconColumn, { [iconColumn]: dimensionValue })
      : { url: undefined, overlayText: undefined };
  if (dimensionTypeName === 'Channel') {
    return {
      ...defaultCell,
      ...iconCell
    };
  }
  if (dimensionTypeName === 'Date' || dimensionTypeName === 'ComparisonDate') {
    if (!dimensionHeader.dateGrouping) {
      return defaultCell;
    }
    const capitalizedDateGrouping = capitalize(dimensionHeader.dateGrouping);
    return {
      label: formatDateLabel(dimensionValue, fiscalCal, capitalizedDateGrouping),
      value: dimensionValue
    };
  }
  if (dimensionTypeName === 'Platform') {
    if (!dimensionValue) {
      return defaultCell;
    }
    const prettyNameMap = {
      audio: 'Streaming Audio',
      facebook: 'Meta',
      google_ads: 'Google Ads',
      linear: 'Linear TV',
      streaming: 'Streaming Video',
      tiktok: 'TikTok',
      twitter: 'X'
    };
    const prettyName = prettyNameMap[dimensionValue.toLowerCase()]
      ? prettyNameMap[dimensionValue.toLowerCase()]
      : capitalize(dimensionValue.replace(/_/g, ' '));
    return {
      label: prettyName,
      value: prettyName,
      ...iconCell
    };
  }

  return defaultCell;
};

export const getCrossChannelColumnMetaData = (
  currencyCode: string
): Partial<Record<CC.ColumnType, ColumnMetaData>> =>
  withRatioAggregators(getCompanyChannelColumnMetaData(columnMetaDataMap, currencyCode));

/**
 * SPARC is a portfolio of companies, and there are cases when they need custom behavior.
 */
export const isSparcCompany = (company: string): boolean =>
  ['reebok', 'nautica', 'eddiebauer', 'lucky', 'aeropostale', 'brooksbrothers'].includes(company);

// The actual shape this function produces: every field of T, plus the same fields again
// suffixed "_other" for the comparison side (see renameComparisonDataKeys below).
export type JoinedRow<T> = T & { [K in keyof T as `${string & K}_other`]: T[K] };

/**
 * Combine primary and comparison into one array, even though they can be of differing lengths/date ranges.
 * Add "_other" suffix to the keys of the comparison data to avoid conflicts.
 */
export const joinPrimaryAndComparisonData = <T extends { date: string }>({
  primaryData,
  comparisonData,
  includeAllComparisonDates = true
}: {
  primaryData: T[];
  comparisonData: T[];
  includeAllComparisonDates?: boolean;
}): JoinedRow<T>[] => {
  try {
    const maxLength = includeAllComparisonDates
      ? Math.max(primaryData.length, comparisonData.length)
      : primaryData.length;

    const joined: Record<string, unknown>[] = [];

    for (let i = 0; i < maxLength; i++) {
      const primaryItem = primaryData[i] || {};
      const comparisonItem = comparisonData[i] || {};
      const renamedComparisonItem = renameComparisonDataKeys(comparisonItem);

      joined.push({
        ...primaryItem,
        ...renamedComparisonItem
      });
    }
    return joined as JoinedRow<T>[];
  } catch (e) {
    console.error('Error joining primary and comparison data', e.message);
    return [];
  }
};

/**
 * Rename keys of object to add "_other" suffix to avoid conflicts when joining data.
 */
const renameComparisonDataKeys = (comparisonDataItem: Record<string, unknown>) => {
  try {
    return R.keys(comparisonDataItem || {}).reduce((acc, key) => {
      acc[`${key}_other`] = (comparisonDataItem || {})[key];
      return acc;
    }, {});
  } catch (e) {
    console.error('Error renaming comparison data keys', e.message);
    return {};
  }
};

export interface JoinedData {
  date: string;
  date_other: string;
  metrics: Metrics;
  metrics_other: Metrics;
}

type JoinedDataWithPercentChange = Omit<JoinedData, 'metrics_other'>;

export const getJoinedDataWithPercentChange = (
  joinedData: JoinedData[]
): JoinedDataWithPercentChange[] => {
  try {
    return joinedData.map(({ date, date_other, metrics, metrics_other }) => ({
      date,
      date_other,
      metrics: calculateMetricsPercentChange(metrics || {}, metrics_other || {})
    }));
  } catch (e) {
    console.error('Error getting joined metrics data with percent change', e.message);
    return [];
  }
};

const calculateMetricsPercentChange = (
  primaryMetrics: Metrics,
  comparisonMetrics: Metrics
): Metrics => {
  try {
    const allMetrics = new Set([...Object.keys(primaryMetrics), ...Object.keys(comparisonMetrics)]);

    const result: Record<string, number | null> = createDefaultCrossChannelMetrics();
    for (const key of Array.from(allMetrics)) {
      // A metric with no value (e.g. a ratio with an empty denominator) has no change either.
      if (primaryMetrics[key] === null || comparisonMetrics[key] === null) {
        result[key] = null;
        continue;
      }
      const primaryValue = primaryMetrics[key] ?? 0;
      const comparisonValue = comparisonMetrics[key] ?? 0;

      // Handle divide by zero
      if (comparisonValue === 0) {
        result[key] = primaryValue === 0 ? 0 : 100;
      } else {
        const percentChange = ((primaryValue - comparisonValue) / comparisonValue) * 100;
        result[key] = Number.isFinite(percentChange) ? percentChange : 0;
      }
    }
    // Null changes draw as gaps; `Metrics` predates nullable values.
    return result as Metrics;
  } catch (e) {
    console.error('Error calculating metrics percent change', e.message);
    return createDefaultCrossChannelMetrics();
  }
};
